import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { take } from 'rxjs/operators';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { HelperService } from '../../../../../core/services/helper.service';
import { InvoiceSharingService } from '../../../../../core/services/invoice-sharing.service';
import { FeatureService } from '../../../../../core/services/permission/feature.service';
import { PermissionService } from '../../../../../core/services/permission/permission.service';
import { DocumentTypes, Job, JobPayment, PurchaseOrder } from '../../../../../data/models';
import { JobsInvoiceSettingsService } from '../../../../../data/repositories/jobs/jobs-invoice-settings.service';
import { RepoJobsService } from '../../../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { ReportService } from '../../../../../reports/report.service';
import { CustomLayoutsService } from '../../../../estimates/custom-layouts/custom-layouts.service';
import { InvoicesReceiptService } from '../../../invoices/invoices-receipt.service';
import { InvoicesService } from '../../../invoices/invoices.service';
import { PurchaseOrdersService } from '../../../purchase-orders/purchase-orders.service';
import { JobInvoicesAttachOrderModalComponent } from './job-invoices-attach-order-modal.component';
import { JobInvoicesDeleteOrderModalComponent } from './job-invoices-delete-order-modal.component';

export type TermsAndConditions = {
    isTermsOnSeparatePage: boolean;
    showCostPlusItemisation: boolean;
    showOrderInstructions: boolean;
    paymentTerms: string;
};

@Component({
    selector: 'app-job-invoices-cost-plus',
    templateUrl: './job-invoices-cost-plus.component.html',
    styleUrls: [
        '../../../../../../../src/app/shared/composable-styles/input.scss',
        '../../../../../../../src/app/shared/composable-styles/button.scss',
    ],
    standalone: false,
})
export class JobInvoicesCostPlusComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() job: Job;
    @Output() invoiceChange = new EventEmitter();
    @Output() showingCustomFooter = new EventEmitter<boolean>();
    purchaseOrders: PurchaseOrder[] = [];
    selectedOrders: PurchaseOrder[] = [];
    filter = 'All';
    taxCode = '';
    tableHeight = 'auto';

    lastSharedInvoiceStatus = '';
    generatingPDF = false;
    pdfUrl = '';
    paymentTotal = 0;
    adjustedTotal = 0;
    remainingTotal = 0;
    remainingPercentage = 0;
    paymentPercentage = 0;
    invoiceTemplatePresets = null;
    termsAndConditions: TermsAndConditions = {
        isTermsOnSeparatePage: false,
        showCostPlusItemisation: false,
        showOrderInstructions: false,
        paymentTerms: '',
    };

    isAllOrderSelected: boolean = false;
    hasUnpaidInvoices: boolean = false;
    #usingAdjustments: boolean;

    statusFilter = signal('All');
    searchText = signal<string>('');
    rows = computed(() => {
        if (this.statusFilter() === 'All') {
            return this.searchFilteredOrders();
        }

        const statusFilteredOrders = this.searchFilteredOrders().filter((order) => {
            if (this.statusFilter() === 'NotInvoiced') {
                return !order.ID_JobPayment;
            }
            if (order.JobPayment && order.JobPayment.Status) {
                return order.JobPayment.Status.toLowerCase() === this.statusFilter().toLowerCase();
            }

            return false;
        });

        return statusFilteredOrders;
    });

    searchFilteredOrders = computed(() => {
        const searchTerm = this.searchText();
        if (searchTerm.length > 0) {
            const searchFilteredOrders = this.purchaseOrders.filter((order) => {
                return (
                    order.OrderNumberString?.toLowerCase().includes(searchTerm) ||
                    order.Description.toLowerCase().includes(searchTerm) ||
                    order.SupplierDescription?.toLowerCase().includes(searchTerm) ||
                    order.JobPayment?.InvoiceNumberString.toLowerCase().includes(searchTerm) ||
                    order.JobPayment?.Description.toLowerCase().includes(searchTerm)
                );
            });

            return searchFilteredOrders;
        }

        return this.purchaseOrders;
    });

    orderCounts = computed(() => {
        const counts = {
            all: 0,
            notInvoiced: 0,
            pending: 0,
            invoiced: 0,
            partreceived: 0,
            received: 0,
        };

        this.searchFilteredOrders().forEach((o) => {
            if (o.IsDeleted || !['Received', 'Completed'].includes(o.OrderStatus)) {
                return;
            }

            counts.all++;
            counts.notInvoiced += !o.ID_JobPayment ? 1 : 0;

            switch (o.JobPayment?.Status) {
                case 'Pending':
                    counts.pending++;
                    break;
                case 'Invoiced':
                    counts.invoiced++;
                    break;
                case 'PartReceived':
                    counts.partreceived++;
                    break;
                case 'Received':
                    counts.received++;
                    break;
            }
        });

        return counts;
    });

    constructor(
        private reportService: ReportService,
        private helperService: HelperService,
        private bxUnverifiedAlertService: BxUnverifiedAlertService,
        private invoiceSharingService: InvoiceSharingService,
        public permissionService: PermissionService,
        public featureService: FeatureService,
        private logger: BxLogger,
        private invoicesService: InvoicesService,
        private invoicesReceiptService: InvoicesReceiptService,
        private purchaseOrdersService: PurchaseOrdersService,
        private customLayoutsService: CustomLayoutsService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private jobsSettingsService: JobsInvoiceSettingsService
    ) {
        this.#usingAdjustments =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';
    }

    handleSearchInput(event: Event) {
        this.clearSelectedOrders();
        const searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.searchText.set(searchTerm);
    }

    ngOnInit(): void {
        this.load();
    }

    updateAllOrderSelected() {
        this.isAllOrderSelected = this.selectedOrders.length !== 0 && this.selectedOrders.length === this.getUnpaidInvoices().length;
    }

    load() {
        this.jobsSettingsService
            .getInvoiceTermsAndConditions()
            .pipe(take(1))
            .subscribe((res) => {
                const termsAndConditions = res.data?.invoiceTermsAndConditions;
                if (termsAndConditions) {
                    this.termsAndConditions = {
                        isTermsOnSeparatePage: termsAndConditions?.isTermsOnSeparatePage ?? false,
                        showCostPlusItemisation: termsAndConditions?.showCostPlusItemisation ?? false,
                        showOrderInstructions: termsAndConditions?.showOrderInstructions ?? false,
                        paymentTerms: termsAndConditions?.paymentTerms ?? '',
                    };
                }
            });

        this.loadInvoices();
        this.clearSelectedOrders();
    }

    loadInvoices() {
        this.purchaseOrders =
            this.job.PurchaseOrders?.filter((p) => {
                return ['Received', 'Completed'].includes(p.OrderStatus);
            }) ?? [];

        this.hasUnpaidInvoices = this.getUnpaidInvoices().length > 0;
    }

    saveJob() {
        if (this.job.InvoiceDueDays || this.job.InvoiceDueDays === 0) this.repoJobs.Save(this.job, 'Default due days updated');
    }

    viewInvoice(invoice: JobPayment) {
        this.invoicesService.viewInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            (error) => ({})
        );
    }

    viewOrder(order: PurchaseOrder) {
        this.purchaseOrdersService.viewOrder(order).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    toggleShowCostPlusItemisation() {
        this.termsAndConditions.showCostPlusItemisation = !this.termsAndConditions.showCostPlusItemisation;
        this.updateTermsAndConditions();
    }

    toggleShowOrderInstructions() {
        this.termsAndConditions.showOrderInstructions = !this.termsAndConditions.showOrderInstructions;
        this.updateTermsAndConditions();
    }

    updateTermsAndConditions() {
        this.jobsSettingsService
            .updateInvoiceTermsAndConditions(
                this.termsAndConditions.isTermsOnSeparatePage,
                this.termsAndConditions.showCostPlusItemisation,
                this.termsAndConditions.showOrderInstructions,
                this.termsAndConditions.paymentTerms
            )
            .pipe(take(1))
            .subscribe((res) => {
                const termsAndConditions = res.data?.updateInvoiceTermsAndConditions;
                if (termsAndConditions) {
                    this.termsAndConditions = {
                        isTermsOnSeparatePage: termsAndConditions?.isTermsOnSeparatePage ?? false,
                        showCostPlusItemisation: termsAndConditions?.showCostPlusItemisation ?? false,
                        showOrderInstructions: termsAndConditions?.showOrderInstructions ?? false,
                        paymentTerms: termsAndConditions?.paymentTerms ?? '',
                    };
                }
            });
    }

    deleteOrderOrInvoice(order: PurchaseOrder) {
        const initialState = {
            order,
        };
        const modalRef = this.helperService.showModalAside(JobInvoicesDeleteOrderModalComponent, { initialState });
        // @ts-ignore TS2532
        modalRef.content.action.pipe(take(1)).subscribe((result) => {
            if (!result.cancelled) {
                if (result.option === 'order') this._deleteOrderFromInvoice(result.order);
                if (result.option === 'invoice') this._deleteInvoice(result.order.JobPayment);
            }
        });
    }

    _deleteOrderFromInvoice(order: PurchaseOrder) {
        const invoice = order.JobPayment;
        // If there's only one order linked to the invoice, and we're trying to unlink them
        // then remove the invoice completely
        // @ts-ignore TS2532
        if (invoice.PurchaseOrders.length === 1 && invoice.PurchaseOrders[0] === order) {
            // @ts-ignore TS2345
            this._deleteInvoice(invoice);
            return;
        }

        // @ts-ignore TS2322
        order.ID_JobPayment = null;
        this.repoJobs.DeleteJobPaymentPurchaseOrderItems(order);
        // @ts-ignore TS2532
        invoice.calculateTotal();
        this.repoJobs.Save(null, 'Order removed').then(() => {
            this.load();
        });
    }

    attachOrderToInvoice() {
        const initialState = {
            job: this.job,
        };
        const modalRef = this.helperService.showModalAside(JobInvoicesAttachOrderModalComponent, { initialState });
        // @ts-ignore TS2532
        modalRef.content.action.pipe(take(1)).subscribe((result) => {
            if (!result.cancelled && result.jobPayment) {
                this._attachOrdersToInvoice(result.jobPayment);
            }
        });
    }

    // @ts-ignore TS7006
    _attachOrdersToInvoice(jobPayment) {
        this.selectedOrders.forEach((order) => {
            order.ID_JobPayment = jobPayment.ID_JobPayment;
            order.CostPlusPercentage = this.job.CostPlusPercentage;
            this.invoicesService.addInvoicePurchaseOrderItems(this.job, order, jobPayment);
        });
        jobPayment.calculateTotal();
        this.repoJobs.Save(null, 'Orders attached').then(() => {
            this.clearSelectedOrders();
            this.load();
        });
    }

    _deleteInvoice(invoice: JobPayment) {
        for (let i = invoice.PurchaseOrders.length - 1; i >= 0; i--) {
            const o = invoice.PurchaseOrders[i];
            // @ts-ignore TS2532
            o.ID_JobPayment = null;
        }
        invoice.calculateTotal();
        invoice.IsDeleted = true;
        this.repoJobs.Save(null, 'Invoice deleted').then(() => {
            this.load();
            this.invoiceChange.emit();
        });
    }

    invoiceSelectedOrders() {
        const params = {
            Description: 'New Invoice',
        };
        this.invoicesService.newInvoice(this.job, params, this.selectedOrders).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    clearSelectedOrders() {
        this.rows().forEach((o) => {
            o.selected = false;
        });
        this.selectedOrders.length = 0;
        this.updateAllOrderSelected();
        this.showingCustomFooter.emit(false);
    }

    selectedChanged(order: PurchaseOrder) {
        if (order.selected) {
            this.selectedOrders.push(order);
        } else {
            const index = this.selectedOrders.indexOf(order);
            if (index !== -1) this.selectedOrders.splice(index, 1);
        }
        this.updateAllOrderSelected();
        this.showingCustomFooter.emit(this.selectedOrders.length > 0);
    }

    printJobClaim() {
        const reportName = this.#usingAdjustments ? 'jobclaimv2' : 'jobclaim';
        const params = {
            reportName,
            reportTitle: 'Invoice Statement',
            params: {
                ID_Job: this.job.ID_Job,
                CalculationMethod: this.job.CalculationMethod,
            },
            fileName: this.job.Number ? this.job.Number + '-InvoiceStatement' : '',
        };
        this.reportService.generate(params);
    }

    printInvoice(invoice: JobPayment) {
        this.invoicesService.generateInvoice(invoice, undefined, true);
    }

    shareInvoice(invoice: JobPayment) {
        this.lastSharedInvoiceStatus = invoice.Status;
        const documentSource = { Id: this.job.ID_Job, Type: 'Job' };
        this.invoiceSharingService.getEmailConfig(invoice, 'pdf').then((emailConfig) => {
            const shareConfig = this.invoiceSharingService.getShareConfig(invoice);
            this.customLayoutsService.openDigitalAcceptanceShareForm(
                emailConfig,
                shareConfig,
                null,
                true,
                DocumentTypes.Invoice,
                false,
                this.job.ID_Lead,
                documentSource,
                true,
                invoice,
                () => {
                    this.handleShareSuccess();
                },
                () => {
                    this.resetInvoiceStatus(invoice);
                }
            );
        });
    }

    // TODO:: move to shared component to slim the product
    emailJobClaim() {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailJobClaim(this.job);
    }

    // TODO:: move to shared component to slim the product
    emailInvoice(invoice: JobPayment) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    viewTermsAndConditions() {
        this.invoicesService.viewTermsAndConditions();
    }

    addReceipt(invoice: JobPayment) {
        this.invoicesReceiptService.addReceipt(invoice).then(
            () => {
                this.repoJobs.Save(null, 'Receipt saved');
                this.invoiceChange.emit();
            },
            () => {
                invoice.entityAspect.rejectChanges();
            }
        );
    }

    handleShareSuccess() {
        this.logger.success('Invoice Shared');
    }

    resetInvoiceStatus(invoice: JobPayment) {
        if (this.lastSharedInvoiceStatus !== invoice.Status) {
            invoice.Status = this.lastSharedInvoiceStatus;
            this.invoicesService.setInvoiceNumber(invoice).then((invoiceWithId) => {
                this.invoicesService.saveInvoice(invoiceWithId);
                this.invoiceChange.emit();
            });
        }
    }

    getUnpaidInvoices() {
        return this.rows().filter((row) => !row.ID_JobPayment);
    }

    selectAllPurchaseOrders() {
        const unpaidInvoices = this.getUnpaidInvoices();
        unpaidInvoices.forEach((row) => {
            row.selected = true;
        });
        this.selectedOrders = [...unpaidInvoices];
        this.updateAllOrderSelected();
        this.showingCustomFooter.emit(this.selectedOrders.length > 0);
    }

    onToggleAllRowsSelected(selectedAll: boolean) {
        if (selectedAll) {
            this.selectAllPurchaseOrders();
        } else {
            this.clearSelectedOrders();
        }
    }
}
